import { findConsecutiveRepeatedCharacter, findRepeatedWord } from './descriptionValidation';

export interface StateExistingRecord {
  _id?: string;
  code?: string;
  name?: string;
  country?: string;
  sortOrder?: number;
}

export interface StateFormInput {
  country: string;
  name: string;
  code: string;
  description?: string;
  sortOrder: number;
}

export const STATE_FIELD_LABELS: Record<string, string> = {
  country: 'Country',
  name: 'State / Province name',
  code: 'Code',
  sortOrder: 'Sort order',
  description: 'Description',
};

export const STATE_MAX_SORT_ORDER = 99999;
const NAME_PATTERN = /^[\p{L}\p{M}][\p{L}\p{M}0-9 .,'()&\-–/]*$/u;
const CODE_PATTERN = /^([A-Z]{2})-([A-Z0-9]{1,5}(?:-[A-Z0-9]{1,3})?)$/;

export const normalizeStateName = (s: unknown) => String(s ?? '').replace(/\s+/g, ' ').trim();

/** Same abbreviation the server uses for subdivisions that are not in the ISO 3166-2 list. */
export function stateCodeAbbreviation(name: string): string {
  const letters = name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9 ]/g, ' ')
    .trim();
  const words = letters.split(/\s+/).filter(Boolean);
  if (!words.length) return '';
  let abbr = words.length > 1 ? words.map((w) => w[0]).join('') : words[0];
  if (abbr.length < 2) abbr = (abbr + words[0].slice(1)).slice(0, 3);
  return abbr.slice(0, 3);
}

/** Code the server will assign: ISO 3166-2 for official subdivisions, otherwise a unique abbreviation. */
export function previewStateCode(country: string, name: string, officialCode: string | undefined, taken: Set<string>) {
  if (!country) return '';
  if (officialCode && !taken.has(officialCode)) return officialCode;
  const abbr = stateCodeAbbreviation(normalizeStateName(name));
  if (!abbr) return '';
  const base = `${country}-${abbr}`;
  if (!taken.has(base)) return base;
  for (let i = 2; i < 100; i += 1) {
    if (!taken.has(`${base}${i}`)) return `${base}${i}`;
  }
  return base;
}

export function nextStateSortOrder(records: StateExistingRecord[], excludeId?: string) {
  return records
    .filter((r) => r._id !== excludeId)
    .reduce((max, r) => Math.max(max, Number(r.sortOrder) || 0), 0) + 1;
}

function repeatedTextError(label: string, text: string) {
  const run = findConsecutiveRepeatedCharacter(text);
  if (run) return `${label} cannot repeat the same character 4+ times ("${run.slice(0, 4)}")`;
  const word = findRepeatedWord(text);
  if (word) return `${label} cannot repeat the same word back to back ("${word}")`;
  return undefined;
}

/** Field errors keyed by form field; `existing` holds the other states of the same country. */
export function validateStateForm(
  form: StateFormInput,
  { existing, editingId }: { existing: StateExistingRecord[]; editingId?: string },
): Record<string, string> {
  const errors: Record<string, string> = {};
  const country = form.country.trim().toUpperCase();
  const name = normalizeStateName(form.name);
  const code = form.code.trim().toUpperCase();
  const description = String(form.description ?? '').trim();
  const others = existing.filter((r) => r._id !== editingId);

  if (!country) errors.country = 'Select the country this state / province belongs to';

  if (!name) errors.name = 'Name is required';
  else if (name.length < 2 || name.length > 80) errors.name = 'Name must be 2–80 characters';
  else if (!NAME_PATTERN.test(name)) errors.name = 'Start with a letter; use letters, numbers, spaces or . , \' ( ) & - /';
  else {
    const repeat = repeatedTextError('Name', name);
    if (repeat) errors.name = repeat;
    else {
      const clash = others.find((r) => normalizeStateName(r.name).toLowerCase() === name.toLowerCase());
      if (clash) errors.name = `"${name}" already exists in ${country} (code ${clash.code})`;
    }
  }

  if (country && name && !errors.name) {
    const match = CODE_PATTERN.exec(code);
    if (!match) errors.code = 'Code could not be generated — adjust the name';
    else if (match[1] !== country) errors.code = `Code must start with ${country}-`;
    else if (others.some((r) => String(r.code || '').toUpperCase() === code)) errors.code = `Code ${code} is already used`;
  }

  const order = Number(form.sortOrder);
  if (!Number.isInteger(order) || order < 1 || order > STATE_MAX_SORT_ORDER) {
    errors.sortOrder = `Enter a whole number between 1 and ${STATE_MAX_SORT_ORDER}`;
  } else {
    const clash = others.find((r) => Number(r.sortOrder) === order);
    if (clash) {
      errors.sortOrder = `Sort order ${order} is already used by ${clash.name} — next available is ${nextStateSortOrder(existing, editingId)}`;
    }
  }

  if (description.length > 500) errors.description = 'Description must be at most 500 characters';
  else if (description) {
    const repeat = repeatedTextError('Description', description);
    if (repeat) errors.description = repeat;
  }
  return errors;
}

/** Country display name from the ISO code, e.g. "AF" → "Afghanistan". */
export function countryDisplayName(code: string): string {
  const iso = String(code || '').trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(iso)) return iso;
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(iso) || iso;
  } catch {
    return iso;
  }
}

/** Windows has no flag emoji glyphs and renders them as the two letters, duplicating the code. */
const FLAGS_RENDER = typeof navigator === 'undefined' || !/Win/i.test(navigator.userAgent || '');

export function countryFlag(code: string): string {
  const iso = String(code || '').trim().toUpperCase();
  if (!FLAGS_RENDER || !/^[A-Z]{2}$/.test(iso)) return '';
  return String.fromCodePoint(...[...iso].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
}
